import { REQUIREMENT_DATA } from "../MembershipJourney/requirementData"
import { ARTICLE_PATHS } from "../MembershipJourney/utils/constants/articleUrls"
import {
  RECIPROCITY_APPLICATION_STATE,
  RECIPROCITY_REVIEW_STATUS,
} from "../../../utils/reciprocityApplication"
import { formatElectionDate } from "../../../utils/applicationElectionDate"
import {
  SECTION_EMPLOYMENT_VERIFICATION_LABEL,
  SECTION_EMPLOYMENT_VERIFICATION_MESSAGE,
} from "../../../utils/sectionEmploymentVerification"

// Reciprocity candidates apply for the same Membership application the
// Associate journey ends with, so reuse its card title and the shared status copy.
const ENROLLMENT = REQUIREMENT_DATA.level3.requirements.enrollment

export const RECIPROCITY_APPLICATION_TITLE = ENROLLMENT.name

export const UNDER_REVIEW_LABEL = "Under Review"
export const UNDER_REVIEW_MESSAGE =
  "Your application is currently being reviewed by Membership Services."
// Same approach as the reinstatement flow (Terminated/ReinstatementSteps): the
// applicant cannot edit a submitted application and cannot reapply, so the
// card tells them to contact Membership Services.
export const CHANGES_REQUESTED_LABEL = "Changes Requested"
export const CHANGES_REQUESTED_MESSAGE =
  "Membership Services requested changes to your application. Contact them using the information below."

const approvedMessage = electionDate => {
  const formatted = formatElectionDate(electionDate)
  if (!formatted) return ENROLLMENT.statuses.approved.message
  return `Your application is approved! You are scheduled for the ${formatted} election, and will receive an official email notification once your election is complete.`
}

/**
 * Card copy per state. `statusKey` picks the badge colors from the Membership
 * Journey status theme so the card reads like the Associate journey's
 * Membership Application requirement.
 */
export const reciprocityApplicationCopy = ({
  state,
  electionDate,
  reviewStatus,
  awaitingSectionEmploymentVerification,
}) => {
  switch (state) {
    case RECIPROCITY_APPLICATION_STATE.DRAFT:
      return {
        statusKey: "in_progress",
        label: "In Progress",
        message:
          "You've started your application for Membership. Pick up where you left off.",
        primaryActionText: "Resume Application",
      }
    case RECIPROCITY_APPLICATION_STATE.IN_REVIEW:
      if (reviewStatus === RECIPROCITY_REVIEW_STATUS.CHANGES_REQUESTED) {
        return {
          statusKey: "in_review",
          label: CHANGES_REQUESTED_LABEL,
          message: CHANGES_REQUESTED_MESSAGE,
          showContact: true,
        }
      }
      if (awaitingSectionEmploymentVerification) {
        return {
          statusKey: "submitted",
          label: SECTION_EMPLOYMENT_VERIFICATION_LABEL,
          message: SECTION_EMPLOYMENT_VERIFICATION_MESSAGE,
        }
      }
      if (reviewStatus === RECIPROCITY_REVIEW_STATUS.UNDER_REVIEW) {
        return {
          statusKey: "submitted",
          label: UNDER_REVIEW_LABEL,
          message: UNDER_REVIEW_MESSAGE,
        }
      }
      return {
        statusKey: "submitted",
        label: ENROLLMENT.statuses.submitted.label,
        message: ENROLLMENT.statuses.submitted.message,
      }
    case RECIPROCITY_APPLICATION_STATE.APPROVED:
      return {
        statusKey: "approved",
        label: ENROLLMENT.statuses.approved.label,
        message: approvedMessage(electionDate),
        secondaryLinkText: ENROLLMENT.statuses.approved.secondaryLinkText,
        secondaryLinkUrl: ARTICLE_PATHS.ELECTION_SCHEDULE,
      }
    case RECIPROCITY_APPLICATION_STATE.ELECTED:
      return {
        statusKey: "complete",
        label: ENROLLMENT.statuses.complete.label,
        message: ENROLLMENT.statuses.complete.message,
      }
    case RECIPROCITY_APPLICATION_STATE.DENIED:
      // The label comes from the Associate journey's Membership Application
      // card ("Declined"). A declined candidate does not reapply from here:
      // Membership Services reauthorizes them first, so there is no action.
      return {
        statusKey: "denied",
        label: ENROLLMENT.statuses.denied.label,
        message:
          "Your application was declined. Contact Membership Services to be reauthorized.",
        showContact: true,
      }
    default:
      return null
  }
}
